import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HeaderProps {
  className?: string;
}

export function Header({ className }: HeaderProps) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    { title: 'Home', href: '/' },
    { title: 'Events', href: '/events' },
    { title: 'Find Your Club', href: '/find-club' },
    { title: 'Parent Access', href: '/parent/auth' },
    { title: 'Coach Access', href: '/coach-auth' },
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const getContextualCTA = () => {
    if (location.pathname === '/') {
      return (
        <Button asChild className="bg-[#f8d613] text-[#111835] hover:bg-[#e6c800]">
          <Link to="/find-club">Sign Up</Link>
        </Button>
      );
    } else if (location.pathname.startsWith('/coach') || location.pathname.startsWith('/admin')) {
      return (
        <Button asChild variant="outline">
          <Link to="/">Back to Home</Link>
        </Button>
      );
    }
    return null;
  };

  return (
    <header className={cn("bg-[#fbfcfc]/95 border-b border-[#e6e8eb] sticky top-0 z-50 backdrop-blur-sm", className)}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <img 
              src="https://lztlmniinqehhxbqftsn.supabase.co/storage/v1/object/public/badge-icons/FutWork_Primary%20Logo-01.png" 
              alt="FutWork Logo" 
              className="h-10"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.title}
                to={item.href}
                className={cn(
                  "text-sm font-medium transition-colors hover:text-[#0248c1]",
                  isActive(item.href) 
                    ? "text-[#0248c1]" 
                    : "text-[#0248c1]/70"
                )}
              >
                {item.title}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex">
            {getContextualCTA()}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#e6e8eb]">
            <nav className="flex flex-col space-y-4">
              {navigationItems.map((item) => (
                <Link
                  key={item.title}
                  to={item.href}
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-[#0248c1]",
                    isActive(item.href) 
                      ? "text-[#0248c1]" 
                      : "text-[#0248c1]/70"
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.title}
                </Link>
              ))}
              <div className="pt-2">
                {getContextualCTA()}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}